import { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from '@/i18n';

const STORAGE_KEY = 'storefront-language';

export const APP_LANGUAGE_OPTIONS = [
  { value: 'en', shortLabel: 'EN', label: 'EN - English' },
  { value: 'ar', shortLabel: 'AR', label: 'AR - العربية' },
  { value: 'ta', shortLabel: 'TA', label: 'TA - தமிழ்' },
  { value: 'hi', shortLabel: 'HI', label: 'HI - हिन्दी' },
];

function normalizeLanguage(input) {
  return String(input || 'en')
    .toLowerCase()
    .split('-')[0];
}

function applyDocumentLanguage(language) {
  if (typeof document === 'undefined') return;
  document.documentElement.lang = language;
  document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
}

export function useAppLanguage() {
  const [language, setLanguage] = useState(
    normalizeLanguage(i18n.resolvedLanguage || i18n.language || 'en'),
  );

  const changeLanguage = useCallback((nextLanguage) => {
    const normalized = normalizeLanguage(nextLanguage);
    setLanguage(normalized);
    i18n.changeLanguage(normalized);
    applyDocumentLanguage(normalized);
    try {
      window.localStorage.setItem(STORAGE_KEY, normalized);
    } catch {
      // Ignore storage write failures.
    }
  }, []);

  useEffect(() => {
    let resolved = normalizeLanguage(i18n.resolvedLanguage || i18n.language || 'en');
    try {
      const stored = window.localStorage.getItem(STORAGE_KEY);
      if (stored) resolved = normalizeLanguage(stored);
    } catch {
      // Ignore storage read failures.
    }
    if (resolved !== language) {
      setLanguage(resolved);
    }
    if (resolved !== normalizeLanguage(i18n.resolvedLanguage || i18n.language || 'en')) {
      i18n.changeLanguage(resolved);
    }
    applyDocumentLanguage(resolved);
  }, [language]);

  return useMemo(
    () => ({
      language,
      setLanguage: changeLanguage,
      options: APP_LANGUAGE_OPTIONS,
    }),
    [changeLanguage, language],
  );
}
